import { Component, lazy, useState } from 'react';
import { Route, Routes, useLocation, matchPath, Navigate, useParams } from 'react-router-dom';
import _ from 'lodash';
import lazyWithRetry from './helpers/lazyWithRetry';
import {
  // public login routes
  CUSTOMIZE_EXPERIENCE,
  FORGET_ROUTE,
  FORGET_ROUTE_SETTINGS,
  INVITE_ROUTE,
  LOGIN_HOME,
  OTP_VERIFY_ROUTE,
  LINK_VERIFY_ROUTE,
  RESET_PASSWORD_ROUTE,
  SIGN_IN_ROUTE,
  SIGNUP_ROUTE,
  TERMS_CONDITION_ROUTE,
  COMMON_HOME,
  // session only routes
  MY_PRODUCTS_ROUTE,
  ADMIN_CURATOR,
  ADMIN_CURATOR_ROOM_ID,
  ADMIN_CURATOR_TYPE_ID,
  ADMIN_DASHBOARD,
  ADMIN_HOME,
  ADMIN_HOME_PATH,
  ADMIN_MY_PROJECTS,
  RENDERS_PREVIEWS_ROUTE,
  RENDERS_PREVIEWS_ROUTE_V2,
  ADMIN_MY_PROPS,
  ADMIN_MY_ROOMS,
  ADMIN_SETTINGS,
  ADMIN_TEAMS,
  COLLECTIONS_ROUTE,
  SHARED_FILES_ROUTE,
  CALENDAR_ROUTE,
  COMMUNITY_ROUTE,
  TRASH_ROUTE,
  PLAY_TO_LEARN_ROUTE,
  REQUESTS_ROUTE,
  AI_ROUTE,
  WORKFLOW_BUILDER_ROUTE,
  SUPPORT_ROUTE,
  MY_ACCOUNT_ROUTE,
  ADD_CREDITS_ROUTE,
  QUIT_ROUTE,
  SEARCH,
  RENDER_DETAILS,
  SEARCH_TEXT,
  COLLECTION_ID,
  ADMIN_MY_PRODUCTS_3DMODAL,
  ADMIN_MY_PRODUCTS_3DMODAL_EACH,
  ADMIN_MY_PRODUCTS_ROOMS,
  ADMIN_MY_PRODUCTS_ROOMS_V2,
  ADMIN_MY_PRODUCTS_TEXTURE,
  ADMIN_MY_PRODUCTS_TEXTURE_V2,
  ADMIN_MY_PRODUCTS_UPLOADS,
  ADMIN_MY_PRODUCTS_UPLOADS_V2,
  ROOM_RENDER,
  PROJECT_RENDER,
  PRODUCT_RENDER,
  ADMIN_MY_ROOMS_PARAMS,
  TEMPLATES_ROUTE,
  NEW_PROJECTS_ROUTE,
  MY_PROJECTS_ROUTE,
  TEMPLATES_DETAILS,
  ADMIN_MY_PRODUCTS_TEXTURE_EDIT,
  ADMIN_MY_PRODUCTS_TEXTURE_EDIT_V2,
  ADMIN_MY_PRODUCTS_TEXTURE_UPLOAD,
  ADMIN_MY_PRODUCTS_ROOMS_DETAILS,
  ADMIN_MY_PRODUCTS_UPLOADS_MOVE_TO_TEXTURE,
  INVITE_INTERNAL_USER,
  INVITE_EXTERNAL_USER,
  MY_TEAM_TEXTURE,
  ADMIN_CURATOR_PROJECT_ID,
  NEW_DESIGN_ROUTE,
  PRODUCT_WEB_EXPERIENCE_PUBLIC,
  RENDERS_DETAILS_BASE_ROUTE,
  COLLECTION_ID_ROUTE_PATH,
  NOTIFICATION_DETAILS_ROUTE,
  OLD_COLLECTION_ID_ROUTE_PATH,
  AUTOMATION_ROUTE,
  AUTOMATION_ROUTE_V2,
  AUTOMATION_EXCEL_HISTORY,
  AUTOMATION_DETAILS,
  TERMS_AND_CONDITION_ROUTE,
  PRIVACY_POLICY_ROUTE,
  TERMS_OF_USE_ROUTE,
  PRODUCT_CONFIGURATORS_ROUTE,
  PRODUCT_CONFIGURATORS_ROUTE_V2,
  PRODUCT_CONFIGURATOR_ROUTE,
  PRODUCT_CONFIGURATOR_TEMPLATES_ROUTE,
  ON_BOARDING_ROUTE,
  AR_VIEWER_ROUTE,
  AR_REDIRECT_ROUTE,
  PUBLIC_AR_REDIRECT_ROUTE,
  ANDROID_CEILING_AR_ROUTE,
  CEILING_CLIP_ROUTE,
  TUTORIALS_ROUTE,
  CONFIGURATOR_VIEWER_ROUTE,
  NEW_REQUEST_ROUTE,
  REQUEST_DETAIL_ROUTE,
  REQUEST_EDIT_ROUTE,
  CREATE_ORGANIZATION_ROUTE,
  PUBLIC_AR_VIEWER_ROUTE,
  ADMIN_MY_PRODUCTS_3DMODAL_EACH_PUBLIC,
  PRODUCT_360_VIEWER_PUBLIC,
  PRODUCT_AI_VIDEO_PUBLIC,
  ADMIN_MY_PRODUCTS_AIVIDEO_PREVIEW,
  PRODUCT_AR_VIEWER_PUBLIC,
  PRODUCT_AR_VIEWER_ROUTE_PUBLIC,
  PRODUCT_AR_VIEWER_ROUTE,
  PRIVATE_AR_ROUTE,
  PRODUCT_VIDEO_PUBLIC,
  CONFIGURATOR_PUBLIC_VIEWER_ROUTE,
  TAB,
  TEMPLATES_ROUTE_V2,
  PROPS_ROUTE_V2,
  ADMIN_MY_PRODUCTS_3DMODAL_V2,
  ADMIN_MY_PROJECTS_V2,
  ADMIN_MY_PRODUCTS_AIVIDEO_RENDER,
  RENDER_DETAILS_V2,
  TEMPLATES_DETAILS_V2,
  GUEST_LOGIN_ROUTE,
  SLA_AGREEMENT_ROUTE,
  PRODUCT_PANNING_VIDEO_PUBLIC,
  UPLOAD_3D_MODEL_HELP_ROUTE,
  RETAIL_AUTOMATION_ROUTE,
  RETAIL_AUTOMATION_DETAILS_ROUTE,
  IMAGINE_AI_ROUTE_V2,
  PRICING_ROUTE,
  SING_UP_GUEST_USER,
  GUEST_USER_REGISTER_ROUTE,
  SIGN_UP_GUEST_USER_WITH_TOKEN,
  MY_FILES_SHARED_FILES_ROUTE,
  MY_PLANS_ROUTE,
  PRODUCT_BUILDER_ROUTE,
  ADMIN_MY_PRODUCTS_3DMODAL_EACH_V2,
  ADMIN_MY_PRODUCTS_TEXTURES_EACH_V2,
  ADMIN_MY_PRODUCTS_3DMODAL_EACH_PUBLIC_V2,
  ADMIN_MY_TEXTURES_3DMODAL_EACH_PUBLIC_V2,
  PRODUCT_360_VIEWER_PUBLIC_V2,
  CONFIGURATOR_PUBLIC_VIEWER_ROUTE_TEXTURE_V2,
  TEXTURE_VIDEO_PUBLIC,
  TEXTURE_360_VIEWER_PUBLIC_V2,
  PRODUCT_VIDEO_PUBLIC_V2,
  PRODUCT_AR_VIEWER_PUBLIC_V2,
  PRODUCT_AI_VIDEO_PUBLIC_V2,
  PRODUCT_AI_VIDEO_PUBLIC_TEXTURE_V2,
  CONFIGURATOR_PUBLIC_VIEWER_ROUTE_V2,
  PRODUCT_3D_VIEWER_PUBLIC_V2,
  TEXTURE_3D_VIEWER_PUBLIC_V2,
  ADMIN_MY_PRODUCTS_MATERIAL_V2,
  PRODUCT_SETUP,
  SHARED_PRODUCT_PAGES_ROUTE_V2,
  RETAIL_CONFIGURATOR_ROUTE,
  RETAIL_ROUTE,
  RETAIL_AUTOMATION_ROUTE_V2,
  RETAIL_CONFIGURATOR_VIEWER_ROUTE,
  RETAIL_AUTOMATION_DETAILS_ROUTE_V2,
  REQUESTS_ROUTE_MODELS,
  REQUESTS_ROUTE_PDP,
  REQUESTS_ROUTE_PDP_DETAIL,
} from './constants/routePathConstants';
import { LayoutWrapper } from './layouts';
import { CuratorGuard } from './common/Guards/CuratorGuard';
import { useUserContext } from './contexts/UserContext';
import AuthGuard from './layouts/AuthGuard';
import AdminGuard from './layouts/AdminGuard';
import { PermissionsGuard } from './common/Guards/PermissionsGuard';
import { WorkflowBuilderGuard } from './common/Guards/WorkflowBuilderGuard';
import { PERMISSION } from './constants/permissionConstants';
import Automation from './screens/Automation';
import AutomationDetails from './screens/AutomationDetails';
// import ConfiguratorTemplates from './screens/ConfiguratorTemplates';
import {
  ADMIN_MY_PROPS_V2,
  CONFIGURATORS_ROUTE,
  CONFIGURATORS_ROUTE_V2,
} from './config/constant/routePathConstant';
import { SETTINGS_TAB } from './constants/constants';
import { LIVE_FURNISH } from './constants/projectConstants.js';
import RequestsV2Edit from './screens/Requests/RequestsV2Edit.js';

const NotFound = lazy(() => import('./screens/NotFound'));
const TermsAndPrivacyPolicy = lazy(() => import('./screens/TermsAndPrivacyPolicy'));

// public routes
const SignIn = lazy(() => import('./screens/Login'));
const SignUp = lazy(() => import('./screens/Register'));
const GuestSignUp = lazy(() => import('./screens/GuestRegister'));
const GuestRegisterWithToken = lazy(() => import('./screens/GuestRegisterWithToken.js'));
const Forget = lazy(() => import('./screens/ForgotPassword'));
const CreatePassword = lazy(() => import('./screens/ResetPassword'));
const OtpVerify = lazy(() => import('./screens/Verify'));
const CustomizeExperience = lazy(() => import('./screens/CustomizeExperience'));
const OnBoarding = lazy(() => import('./screens/OnBoarding'));
const InvitePage = lazy(() => import('./screens/Invitation'));
const GuestLoginPage = lazy(() => import('./screens/GuestLogin'));
const GuestUserRegister = lazy(() => import('./screens/GuestUserRegister'));
const CreateOrganization = lazy(() => import('./screens/CreateOrganization'));
//const InvitePage = lazy(() => import('./pages/invitation'));

// admin routes
// const AddCredits = lazy(() => import('./screens/AddCredits'));
const AdminDashboard = lazy(() => import('./screens/AdminDashboard'));
const AdminNewDashboard = lazy(() => import('./screens/NewDashboard/Dashboard.js'));
const ProductDetails = lazy(() => import('./screens/NewProductUI/ProductDetails.js'));
const ProductDetailsV2 = lazy(() => import('./screens/NewProductUIV2/ProductDetailsV2.js'));
const TextureDetailsV2 = lazy(() => import('./screens/NewProductUIV2/TextureDetailsV2.js'));
const PublicProductDetails = lazy(() => import('./screens/NewProductUI/PublicProductDetails.js'));
const PublicProductDetailsV2 = lazy(() =>
  import('./screens/NewProductUIV2/PublicProductDetailsV2.js')
);
const PublicTextureDetailsV2 = lazy(() =>
  import('./screens/NewProductUIV2/PublicTextureDetailsV2.js')
);
const Public360Viewer = lazy(() => import('./screens/NewProductUI/Public360Viewer.js'));
const Public360ViewerV2 = lazy(() => import('./screens/NewProductUIV2/Public360ViewerV2.js'));
const PublicAIVideo = lazy(() => import('./screens/NewProductUI/PublicAIVideo.js'));
const PublicVideo = lazy(() => import('./screens/NewProductUI/PublicVideo.js'));
const PublicPanningVideo = lazy(() => import('./screens/NewProductUI/PublicPanningVideo.js'));
const PublicARViewer = lazyWithRetry(() => import('./screens/NewProductUI/PublicARViewer.js'));
// const AdminTeams = lazy(() => import('./screens/AdminTeams'));
const Calendar = lazy(() => import('./screens/Calendar'));
const Collection = lazy(() => import('./screens/Collection'));
const Collections = lazy(() => import('./screens/Collections'));
const Community = lazy(() => import('./screens/Community'));
const Curator = lazy(() => import('./screens/Curator'));
const MyAccount = lazy(() => import('./screens/MyAccount'));
const MyProducts = lazy(() => import('./screens/MyProducts'));
const MyProducts3dModel = lazy(() => import('./screens/MyProducts3dModel.js'));
const MyProductsV2 = lazy(() => import('./screens/DesignV2Components/MyProducts.js'));
const MyProductsRooms = lazy(() => import('./screens/MyProductsRooms'));
const MyProductsTextures = lazy(() => import('./screens/MyProductsTextures'));
const MyProductsTextureEdit = lazy(() => import('./screens/MyProductsTextureEdit'));
const MyProductsUploads = lazy(() => import('./screens/MyProductsUploads.js'));
const MyProductsWebView = lazy(() => import('./screens/MyProductsWebView.js'));
const MyProjects = lazy(() => import('./screens/MyProjects'));
const MyProjectsV2 = lazy(() => import('./screens/DesignV2Components/MyFiles'));
const MattressBuilder = lazy(() => import('./screens/DesignV2Components/MattressBuilder'));
const RetailConfigurator = lazy(() => import('./screens/RetailConfigurator.js'));
const RetailConfiguratorViewer = lazy(() => import('./screens/RetailConfiguratorViewer.js'));
const ProductConfigurators = lazy(() => import('./screens/ProductConfigurators'));
const ImagineAI = lazy(() => import('./screens/ImagineAI'));
const Configurator = lazy(() => import('./screens/Configurator'));
const MyProps = lazy(() => import('./screens/MyProps'));
const NewDesign = lazy(() => import('./screens/NewDesign'));
const MyRooms = lazy(() => import('./screens/MyRooms'));
const Library = lazy(() => import('./screens/DesignV2Components/Library.js'));
const Retail = lazy(() => import('./screens/DesignV2Components/Retail.js'));
const PlayToLearn = lazy(() => import('./screens/PlayToLearn'));
const ProductRender = lazy(() => import('./screens/ProductRender'));
const ProjectRender = lazy(() => import('./screens/ProjectRender'));
const Quit = lazy(() => import('./screens/Quit'));
const RenderDetails = lazy(() => import('./screens/RenderDetails'));
const RendersPreviews = lazy(() => import('./screens/RendersPreviews'));
const Requests = lazy(() => import('./screens/Requests/RequestsV2.js'));
const RequestDetails = lazy(() => import('./screens/Requests/RequestDetails'));
const NewRequest = lazy(() => import('./screens/Requests/NewRequest'));
const RoomRender = lazy(() => import('./screens/RoomRender'));
const Search = lazy(() => import('./screens/Search'));
const Settings = lazy(() => import('./screens/Settings'));
// const SharedFiles = lazy(() => import('./screens/SharedFiles'));
const SharedFiles = lazy(() => import('./screens/DesignV2Components/SharedFiles'));
const TabCommon = lazy(() => import('./common/Tab/index.js'));
const Support = lazy(() => import('./screens/Support'));
const Trash = lazy(() => import('./screens/DesignV2Components/Trash'));
const Pricing = lazy(() => import('./screens/Pricing'));
const MyPlans = lazy(() => import('./screens/MyPlans'));
const Tutorials = lazy(() => import('./screens/Tutorials'));
// const AppleMerchant = lazy(() => import('./screens/AppleMerchant'));
const RendersPreviewsDashboard = lazy(() => import('./screens/RendersPreviews'));
const TemplatesDetails = lazy(() => import('./screens/TemplatesDetails'));
const TemplatesDetailsRoom = lazy(() => import('./screens/TemplatesDetailsRoom'));
const InviteInternalUser = lazy(() => import('./screens/InviteInternalUser'));
const InviteExternalUser = lazy(() => import('./screens/InviteExternalUser'));
const MyTeamTexture = lazy(() => import('./screens/MyTeamTexture'));
const PublicWebExperience = lazy(() => import('./screens/PublicWebExperience'));
const NotificationDetails = lazy(() => import('./common/NotificationDetails/NotificationDetails'));
// Automation
const AutomationExcelList = lazy(() => import('./screens/AutomationExcelUploadHistory'));
const Configurators = lazy(() => import('./screens/Configurators'));
// AR Viewer
const ARViewer = lazyWithRetry(() => import('./common/ARViewer/ArViewer'));
const ArRedirect = lazyWithRetry(() => import('./common/ARViewer/ArRedirect'));
const AndroidCeilingAR = lazyWithRetry(() => import('./common/ARViewer/AndroidCeilingGyroAR'));
const CeilingClip = lazyWithRetry(() => import('./common/ARViewer/CeilingClip'));
const PublicProductARViewer = lazyWithRetry(() => import('./common/ARViewer/PublicProductARViewer'));
const PrivateARViewer = lazyWithRetry(() => import('./common/ARViewer/PrivateARViewer'));
const ConfiguratorViewer = lazy(() =>
  import('./components/Configurator/ConfiguratorViewer/ConfiguratorViewer')
);
const ConfiguratorPublicViewer = lazy(() =>
  import('./components/Configurator/ConfiguratorViewer/ConfiguratorPublicViewer')
);
// AI Video
const AIVideoPreview = lazy(() => import('./screens/NewProductUI/AIVideoPreview'));

// Image AI
const ImageAI = lazy(() => import('./screens/AI/ImageAi'));

// Workflow Builder
const WorkflowBuilder = lazy(() => import('./screens/WorkflowBuilder.js'));

const RetailAutomation = lazy(() => import('./components/RetailAutomation/RetailAutomation.js'));

const ConfiguratorPublicViewerTextureV2 = lazy(() =>
  import('./screens/NewProductUIV2/ConfiguratorPublicViewerTextureV2.js')
);
const ConfiguratorPublicViewerV2 = lazy(() =>
  import('./screens/NewProductUIV2/ConfiguratorPublicViewerV2.js')
);
const PublicVideoTextureV2 = lazy(() => import('./screens/NewProductUIV2/PublicVideoTextureV2.js'));
const PublicARViewerV2 = lazy(() => import('./screens/NewProductUIV2/PublicARViewerV2.js'));
const PublicProduct3DViewerV2 = lazy(() =>
  import('./screens/NewProductUIV2/PublicProduct3DViewerV2.js')
);
const PublicTexture3DViewerV2 = lazy(() =>
  import('./screens/NewProductUIV2/PublicTexture3DViewerV2.js')
);
const PublicVideoV2 = lazy(() => import('./screens/NewProductUIV2/PublicVideoV2.js'));
const PublicAIVideoV2 = lazy(() => import('./screens/NewProductUIV2/PublicAIVideoV2.js'));
const PublicAIVideoTextureV2 = lazy(() =>
  import('./screens/NewProductUIV2/PublicAIVideoTextureV2.js')
);
const Public360ViewerTextureV2 = lazy(() =>
  import('./screens/NewProductUIV2/Public360ViewerTextureV2.js')
);

const ProductSetup = lazy(() => import('./screens/ProductSetup/ProductSetupScreen.js'));

const RedirectToNewCollectionPath = () => {
  const { collectionId } = useParams();
  const url = COLLECTION_ID_ROUTE_PATH.replace(':collectionId', collectionId);
  return <Navigate to={url} replace />;
};

//public routes
const LOGIN_ROUTES = [
  { path: INVITE_ROUTE, Component: InvitePage, key: 'invite' },
  {
    path: GUEST_LOGIN_ROUTE,
    Component: GuestLoginPage,
    key: 'guest-login',
    canGuestUserAccess: true,
  },
  {
    Component: AuthGuard,
    canGuestUserAccess: true,
    routes: [
      { path: LOGIN_HOME, Component: SignIn, key: 'login', canGuestUserAccess: true },
      { path: SIGN_IN_ROUTE, Component: SignIn, key: 'sign-in', canGuestUserAccess: true },
      { path: SIGNUP_ROUTE, Component: SignUp, key: 'register' },
      { path: SING_UP_GUEST_USER, Component: GuestSignUp, key: 'guest-register' },
      // { path: SIGN_UP_GUEST_USER_WITH_TOKEN, Component: GuestRegisterWithToken, key: 'guest-register-with-token' },
      { path: OTP_VERIFY_ROUTE, Component: OtpVerify, key: 'otp-verify' },
      { path: LINK_VERIFY_ROUTE, Component: OtpVerify, key: 'otp-verify' }, // click on link in email to verify route
      // { path: CUSTOMIZE_EXPERIENCE, Component: CustomizeExperience, key: 'customize-experience' },
      { path: ON_BOARDING_ROUTE, Component: OnBoarding, key: 'onboarding' },
      {
        path: CREATE_ORGANIZATION_ROUTE,
        Component: CreateOrganization,
        key: 'create-organization',
      },
      {
        path: ADMIN_MY_PRODUCTS_3DMODAL_EACH_PUBLIC,
        Component: PublicProductDetails,
        key: 'products/each-model/public/',
      },
      {
        path: ADMIN_MY_PRODUCTS_3DMODAL_EACH_PUBLIC_V2,
        Component: PublicProductDetailsV2,
        key: 'products/v2/each-model/public/',
      },
      {
        path: ADMIN_MY_TEXTURES_3DMODAL_EACH_PUBLIC_V2,
        Component: PublicTextureDetailsV2,
        key: 'textures/v2/each-model/public/',
      },
      {
        path: PRODUCT_360_VIEWER_PUBLIC,
        Component: Public360Viewer,
        key: 'product/public/360-viewer/',
      },
      {
        path: PRODUCT_360_VIEWER_PUBLIC_V2,
        Component: Public360ViewerV2,
        key: 'product/v2/public/360-viewer/',
      },
      {
        path: TEXTURE_360_VIEWER_PUBLIC_V2,
        Component: Public360ViewerTextureV2,
        key: 'texture/v2/public/360-viewer/',
      },
      {
        path: PRODUCT_AI_VIDEO_PUBLIC,
        Component: PublicAIVideo,
        key: 'product/public/ai-video/',
      },
      {
        path: PRODUCT_AI_VIDEO_PUBLIC_V2,
        Component: PublicAIVideoV2,
        key: 'v2/product/public/ai-video/',
      },
      {
        path: PRODUCT_AI_VIDEO_PUBLIC_TEXTURE_V2,
        Component: PublicAIVideoTextureV2,
        key: 'v2/texture/public/ai-video/',
      },
      {
        path: PRODUCT_VIDEO_PUBLIC,
        Component: PublicVideo,
        key: 'product/public/video/',
      },
      {
        path: PRODUCT_VIDEO_PUBLIC_V2,
        Component: PublicVideoV2,
        key: 'v2/product/public/video/',
      },
      {
        path: TEXTURE_VIDEO_PUBLIC,
        Component: PublicVideoTextureV2,
        key: 'texture/public/video/',
      },
      {
        path: PRODUCT_PANNING_VIDEO_PUBLIC,
        Component: PublicPanningVideo,
        key: 'product/public/panning-video/',
      },
      {
        path: PRODUCT_AR_VIEWER_PUBLIC,
        Component: PublicARViewer,
        key: 'product/public/ar-viewer/',
      },
      {
        path: PRODUCT_AR_VIEWER_PUBLIC_V2,
        Component: PublicARViewerV2,
        key: 'v2/product/public/ar-viewer/',
      },
      {
        path: PRODUCT_3D_VIEWER_PUBLIC_V2,
        Component: PublicProduct3DViewerV2,
        key: 'v2/product/public/3d-viewer/',
      },
      {
        path: TEXTURE_3D_VIEWER_PUBLIC_V2,
        Component: PublicTexture3DViewerV2,
        key: 'v2/texture/public/3d-viewer/',
      },
    ],
  },
];

export const ADMIN_NO_SIDEBAR_ROUTES = [
  {
    Component: AdminGuard,
    routes: [
      {
        Component: PermissionsGuard,
        componentProps: {
          permissions: [PERMISSION.CAN_INVITE_MEMBERS],
          redirectUrl: `${ADMIN_SETTINGS}?activeTab=${SETTINGS_TAB.MEMBERS}`,
        },
        routes: [
          {
            path: INVITE_INTERNAL_USER,
            Component: InviteInternalUser,
            key: 'invite_internal_user',
          },
          // {
          //   path: INVITE_EXTERNAL_USER,
          //   Component: InviteExternalUser,
          //   key: 'invite_external_user',
          // },
        ],
      },
    ],
  },
];

export const ADMIN_ROUTES = [
  {
    Component: AdminGuard,
    canGuestUserAccess: true,
    canGuestLoginUserAccess: true,
    routes: [
      {
        path: '',
        Component: AdminNewDashboard,
        key: 'home',
        canGuestUserAccess: true,
        canGuestLoginUserAccess: true,
        routes: [
          {
            path: NOTIFICATION_DETAILS_ROUTE,
            Component: NotificationDetails,
            key: 'NotificationDetails',
          },
        ],
      },
      { path: LINK_VERIFY_ROUTE, Component: AdminNewDashboard, key: 'otp-verify' }, // click on link in email to verify route
      { path: ADMIN_HOME_PATH, Component: AdminNewDashboard, key: 'home_path' },
      {
        path: ADMIN_DASHBOARD,
        Component: AdminNewDashboard,
        key: 'dashboard',
        canGuestLoginUserAccess: true,
      },
      { path: MY_PRODUCTS_ROUTE, Component: MyProducts, key: 'products' },
      { path: ADMIN_SETTINGS, Component: Settings, key: 'settings', canGuestUserAccess: true },
      // { path: ADMIN_TEAMS, Component: AdminTeams, key: 'teams' },
      // this path needs to be before Render because it is a special /project/createNew/:projectId so it catches the dynamic :projectId path first
      { path: NEW_PROJECTS_ROUTE, Component: MyProjects, key: 'project' },
      // this path needs to be before MyProjects so it catches the dynamic :projectId path first
      { path: PROJECT_RENDER, Component: ProjectRender, key: 'ProjectRender' },
      {
        path: ADMIN_MY_PROJECTS,
        Component: MyProjects,
        key: 'project',
      },
      {
        path: ADMIN_MY_PROJECTS_V2,
        Component: MyProjectsV2,
        key: 'my-files',
        canGuestUserAccess: true,
        canGuestLoginUserAccess: true,
      },
      {
        path: ADMIN_MY_PRODUCTS_3DMODAL_V2,
        Component: MyProductsV2,
        key: 'myproductsv2',
        canGuestUserAccess: true,
      },
      { path: ADMIN_MY_PRODUCTS_3DMODAL, Component: MyProducts3dModel, key: 'products/my-models' },
      {
        path: ADMIN_MY_PRODUCTS_ROOMS,
        Component: MyProductsRooms,
        key: 'products/my-rooms',
        routes: [
          {
            path: ':roomId',
            Component: TemplatesDetailsRoom,
            key: 'products-rooms-details',
          },
        ],
      },
      {
        path: ADMIN_MY_PRODUCTS_ROOMS_V2,
        Component: MyProductsV2,
        key: 'my-products/my-rooms',
        routes: [
          {
            path: ':roomId',
            Component: TemplatesDetailsRoom,
            key: 'products-rooms-details',
          },
        ],
      },
      {
        path: ADMIN_MY_PRODUCTS_TEXTURE,
        Component: MyProductsTextures,
        key: 'products/textures',
      },
      {
        path: ADMIN_MY_PRODUCTS_TEXTURE_V2,
        Component: MyProductsV2,
        key: 'my-products/textures',
        canGuestUserAccess: true,
      },
      {
        path: ADMIN_MY_PRODUCTS_MATERIAL_V2,
        Component: MyProductsV2,
        key: 'my-products/materials',
        canGuestUserAccess: true,
      },
      {
        path: ADMIN_MY_PRODUCTS_UPLOADS,
        Component: MyProductsUploads,
        key: 'products/custom-maps',
      },
      {
        path: ADMIN_MY_PRODUCTS_UPLOADS_V2,
        Component: MyProductsV2,
        key: 'my-products/custom-maps',
      },
      {
        path: ADMIN_MY_PRODUCTS_3DMODAL_EACH,
        Component: ProductDetails,
        key: 'products/each-model',
        canGuestUserAccess: true,
      },
      {
        path: ADMIN_MY_PRODUCTS_3DMODAL_EACH_V2,
        Component: ProductDetailsV2,
        key: 'products/each-model-v2',
        canGuestUserAccess: true,
        canGuestLoginUserAccess: true,
      },
      {
        path: ADMIN_MY_PRODUCTS_TEXTURES_EACH_V2,
        Component: TextureDetailsV2,
        key: 'products/each-texture-v2',
        canGuestUserAccess: true,
        canGuestLoginUserAccess: true,
      },
      {
        path: RENDERS_PREVIEWS_ROUTE,
        Component: RendersPreviews,
        key: 'renders-previews',
        canGuestUserAccess: true,
        routes: [
          {
            path: ':renderId',
            Component: RenderDetails,
            key: 'RenderDetails',
            canGuestUserAccess: true,
          },
        ],
      },
      {
        path: RENDERS_PREVIEWS_ROUTE_V2,
        Component: MyProjectsV2,
        key: 'my-files-renders-previews',
        canGuestUserAccess: true,
        canGuestLoginUserAccess: true,
        routes: [
          {
            path: ':renderId',
            Component: RenderDetails,
            key: 'RenderDetails1',
            canGuestUserAccess: true,
            canGuestLoginUserAccess: true,
          },
          {
            path: ':id',
            Component: RenderDetails,
            key: 'RenderDetails2',
            canGuestUserAccess: true,
            canGuestLoginUserAccess: true,
          },
        ],
      },
      { path: ADMIN_MY_PROPS, Component: MyProps, key: 'list-members' },
      { path: ADMIN_MY_PROPS_V2, Component: Library, key: 'library-list-members' },
      { path: ADMIN_MY_ROOMS, Component: MyRooms, key: 'rooms' },
      {
        path: TEMPLATES_ROUTE,
        Component: MyRooms,
        key: 'templates',
        routes: [{ path: ':roomId', Component: TemplatesDetails, key: 'TemplatesDetails' }],
      },
      {
        path: TEMPLATES_ROUTE_V2,
        Component: Library,
        key: 'libraryTemplates',
        routes: [
          {
            path: ':roomId',
            Component: TemplatesDetails,
            key: 'TemplatesDetails',
            canGuestLoginUserAccess: true,
          },
        ],
        canGuestLoginUserAccess: true,
      },
      {
        path: PROPS_ROUTE_V2,
        Component: Library,
        key: 'libraryProps',
        canGuestLoginUserAccess: true,
      },
      {
        path: SHARED_PRODUCT_PAGES_ROUTE_V2,
        Component: MyProductsV2,
        key: 'myProductSharedProductPage',
        canGuestLoginUserAccess: true,
      },
      {
        path: COLLECTIONS_ROUTE,
        Component: Collections,
        key: 'collections',
      },
      {
        path: RETAIL_ROUTE,
        Component: Retail,
        key: 'retail',
        routes: [
          {
            path: 'configurator',
            Component: RetailConfigurator,
            key: 'RetailConfigurator',
            canGuestLoginUserAccess: false,
          },
          {
            path: 'automation',
            Component: RetailAutomation,
            key: 'RetailAutomation',
            canGuestLoginUserAccess: false,
          },
        ],
        canGuestLoginUserAccess: false,
      },
      // {
      //   path: SHARED_FILES_ROUTE,
      //   Component: SharedFiles,
      //   key: 'shared-files',
      //   routes: [
      //     { path: 'templates/:roomId', Component: TemplatesDetails, key: 'TemplatesDetails' },
      //     { path: 'images/:renderId', Component: RenderDetails, key: 'RenderDetails' },
      //   ],
      // },
      {
        path: TAB,
        Component: TabCommon,
        key: 'tab-files',
      },
      { path: CALENDAR_ROUTE, Component: Calendar, key: 'calendar' },
      { path: COMMUNITY_ROUTE, Component: Community, key: 'community' },
      {
        path: COLLECTION_ID_ROUTE_PATH,
        Component: Collection,
        key: 'collectionsId',
        routes: [
          { path: 'templates/:roomId', Component: TemplatesDetails, key: 'TemplatesDetails' },
          { path: 'renders/:renderId', Component: RenderDetails, key: 'RenderDetails' },
        ],
      },
      {
        path: OLD_COLLECTION_ID_ROUTE_PATH,
        Component: RedirectToNewCollectionPath,
      },
      { path: TRASH_ROUTE, Component: Trash, key: 'trash' },
      // { path: PRICING_ROUTE, Component: Pricing, key: 'pricing', canGuestLoginUserAccess: true },
      { path: MY_PLANS_ROUTE, Component: MyPlans, key: 'myPlans', canGuestLoginUserAccess: false },
      {
        path: TUTORIALS_ROUTE,
        Component: Tutorials,
        key: 'tutorials',
        canGuestLoginUserAccess: true,
      },
      { path: PLAY_TO_LEARN_ROUTE, Component: PlayToLearn, key: 'play-to-learn' },
      {
        path: REQUESTS_ROUTE,
        Component: Requests,
        key: 'requests',
        routes: [
          {
            path: 'models',
            Component: Requests,
            key: 'RequestsModels',
            canGuestUserAccess: true,
          },
          {
            path: 'pdp',
            Component: Requests,
            key: 'RequestsPDP',
            canGuestUserAccess: true,
          },
        ],
        canGuestUserAccess: true,
      },
      {
        path: REQUEST_DETAIL_ROUTE,
        Component: RequestDetails,
        key: 'request-detail',
        canGuestUserAccess: true,
      },
      { path: SUPPORT_ROUTE, Component: Support, key: 'support' },
      { path: MY_ACCOUNT_ROUTE, Component: MyAccount, key: 'my-account' },
      { path: QUIT_ROUTE, Component: Quit, key: 'quit' },
      { path: SEARCH, Component: Search, key: 'search' },
      { path: SEARCH_TEXT, Component: Search, key: 'search_text' },
      { path: RENDERS_PREVIEWS_ROUTE, Component: RendersPreviewsDashboard, key: 'search_text' },
      { path: ROOM_RENDER, Component: RoomRender, key: 'RoomRender' },
      { path: PRODUCT_RENDER, Component: ProductRender, key: 'ProductRender' },
      { path: NEW_DESIGN_ROUTE, Component: NewDesign, key: 'new_design' },
      { path: ADMIN_MY_ROOMS_PARAMS, Component: MyRooms, key: 'rooms_param' },
      { path: MY_TEAM_TEXTURE, Component: MyTeamTexture, key: 'my_team-texture' },
      {
        path: IMAGINE_AI_ROUTE_V2,
        Component: MyProjectsV2,
        key: 'my-files-imagine-ai',
        canGuestUserAccess: true,
      },
      {
        path: PRODUCT_SETUP,
        Component: ProductSetup,
        key: 'products-setup',
        canGuestUserAccess: true,
        canGuestLoginUserAccess: true,
      },
    ],
  },
  {
    Component: AdminGuard, // PermissionsGuard,
    componentProps: {
      permissions: [], //[PERMISSION.CAN_CREATE_AUTOMATION],
      redirectUrl: '/',
    },
    canGuestUserAccess: true,
    routes: [
      { path: AUTOMATION_ROUTE, Component: Automation, key: 'automation' },
      {
        path: AUTOMATION_EXCEL_HISTORY,
        Component: AutomationExcelList,
        key: 'automation_excel_list',
      },
      { path: AUTOMATION_DETAILS, Component: AutomationDetails, key: 'automation-details' },
    ],
  },
  {
    Component: AdminGuard, // PermissionsGuard,
    componentProps: {
      permissions: [], //[PERMISSION.CAN_CREATE_AUTOMATION],
      redirectUrl: '/',
    },
    canGuestUserAccess: true,
    routes: [
      { path: AUTOMATION_ROUTE_V2, Component: MyProjectsV2, key: 'my-files-automation' },
      {
        path: AUTOMATION_EXCEL_HISTORY,
        Component: AutomationExcelList,
        key: 'automation_excel_list',
      },
      { path: AUTOMATION_DETAILS, Component: AutomationDetails, key: 'automation-details' },
    ],
  },
  {
    Component: PermissionsGuard,
    componentProps: {
      permissions: [PERMISSION.CONFIGURATORS],
      redirectUrl: '/',
    },
    canGuestUserAccess: true,
    routes: [{ path: CONFIGURATORS_ROUTE, Component: Configurators, key: 'configurators' }],
  },
  {
    Component: AdminGuard, // PermissionsGuard,
    componentProps: {
      permissions: [], // [PERMISSION.CONFIGURATORS],
      redirectUrl: '/',
    },
    canGuestUserAccess: true,
    routes: [
      { path: CONFIGURATORS_ROUTE_V2, Component: MyProjectsV2, key: 'my-files-configurators' },
    ],
  },
  {
    Component: PermissionsGuard,
    componentProps: {
      permissions: [PERMISSION.PRODUCT_CONFIGURATORS],
      redirectUrl: '/',
    },
    canGuestUserAccess: true,
    routes: [
      {
        path: PRODUCT_CONFIGURATORS_ROUTE,
        Component: MyProjectsV2,
        key: 'product_configurators',
      },
      // {
      //   path: PRODUCT_CONFIGURATOR_TEMPLATES_ROUTE,
      //   Component: ConfiguratorTemplates,
      //   key: 'configurator_templates',
      // },
    ],
  },
  {
    Component: AdminGuard, //PermissionsGuard,
    componentProps: {
      permissions: [], //[PERMISSION.PRODUCT_CONFIGURATORS],
      redirectUrl: '/',
    },
    canGuestUserAccess: true,
    routes: [
      {
        path: PRODUCT_CONFIGURATORS_ROUTE_V2,
        Component: MyProjectsV2,
        key: 'my-files-product-configurators',
      },
    ],
  },
  // {
  //   Component: AdminGuard, //PermissionsGuard,
  //   componentProps: {
  //     permissions: [PERMISSION.RETAIL_AUTOMATION], //[PERMISSION.PRODUCT_CONFIGURATORS],
  //     redirectUrl: '/',
  //   },
  //   canGuestUserAccess: true,
  //   routes: [
  //     {
  //       path: RETAIL_AUTOMATION_ROUTE,
  //       Component: MyProjectsV2,
  //       key: 'my-files-retail-automation',
  //     },
  //   ],
  // },

  {
    Component: AdminGuard, //PermissionsGuard,
    componentProps: {
      permissions: [],
      redirectUrl: '/',
    },
    canGuestUserAccess: true,
    routes: [
      {
        path: MY_FILES_SHARED_FILES_ROUTE,
        Component: MyProjectsV2,
        key: 'my-files-shared-files-route',
        routes: [
          { path: 'templates/:roomId', Component: TemplatesDetails, key: 'TemplatesDetails' },
          { path: 'images/:renderId', Component: RenderDetails, key: 'RenderDetails' },
        ],
      },
    ],
  },
  {
    Component: AdminGuard, //PermissionsGuard,
    componentProps: {
      permissions: [PERMISSION.PRODUCT_BUILDER], //[PERMISSION.PRODUCT_CONFIGURATORS],
      redirectUrl: '/',
    },
    canGuestUserAccess: true,
    routes: [
      {
        path: PRODUCT_BUILDER_ROUTE,
        Component: MattressBuilder,
        key: 'my-files-product-builder',
      },
    ],
  },
];

export const NO_LAYOUT_ROUTES = [
  {
    path: LOGIN_HOME,
    Component: SignIn,
    key: 'login-admin',
    canGuestUserAccess: true,
  },
  { path: INVITE_ROUTE, Component: InvitePage, key: 'invite' },
  {
    path: GUEST_LOGIN_ROUTE,
    Component: GuestLoginPage,
    key: 'guest-login',
    canGuestUserAccess: true,
  },
  { path: GUEST_USER_REGISTER_ROUTE, Component: GuestUserRegister, key: 'guest-user-register' },
  { path: FORGET_ROUTE, Component: Forget, key: 'forget' },
  { path: FORGET_ROUTE_SETTINGS, Component: Forget, key: 'forget' }, // this one is slightly different so we can redirect back to MyDetails instead of Sign-in on success
  { path: RESET_PASSWORD_ROUTE, Component: CreatePassword, key: 'reset' },
  {
    path: TERMS_AND_CONDITION_ROUTE,
    Component: TermsAndPrivacyPolicy,
    key: 'tnc',
    canGuestUserAccess: true,
    canGuestLoginUserAccess: true,
  },
  {
    path: LIVE_FURNISH.privacyPolicy, // PRIVACY_POLICY_ROUTE,
    Component: TermsAndPrivacyPolicy,
    key: 'privacy-policy',
    canGuestUserAccess: true,
    canGuestLoginUserAccess: true,
    newTab: true,
  },
  {
    path: UPLOAD_3D_MODEL_HELP_ROUTE,
    Component: TermsAndPrivacyPolicy,
    key: 'upload-3d-model-help',
    canGuestUserAccess: true,
    canGuestLoginUserAccess: true,
  },
  {
    path: LIVE_FURNISH.termsOfUse, // TERMS_OF_USE_ROUTE,
    Component: TermsAndPrivacyPolicy,
    key: 'termsofuse',
    canGuestUserAccess: true,
    canGuestLoginUserAccess: true,
    newTab: true,
  },
  {
    path: SLA_AGREEMENT_ROUTE,
    Component: TermsAndPrivacyPolicy,
    key: 'sla-agreement',
    canGuestUserAccess: true,
    canGuestLoginUserAccess: true,
  },
  { path: ON_BOARDING_ROUTE, Component: OnBoarding, key: 'onboarding' },
  { path: CREATE_ORGANIZATION_ROUTE, Component: CreateOrganization, key: 'create-organization' },
  {
    path: ADMIN_MY_PRODUCTS_3DMODAL_EACH_PUBLIC,
    Component: PublicProductDetails,
    key: 'products/each-model/public/',
    canGuestLoginUserAccess: true,
  },
  {
    path: ADMIN_MY_PRODUCTS_3DMODAL_EACH_PUBLIC_V2,
    Component: PublicProductDetailsV2,
    key: 'products/v2/each-model/public/',
    canGuestUserAccess: true,
    canGuestLoginUserAccess: true,
  },
  {
    path: ADMIN_MY_TEXTURES_3DMODAL_EACH_PUBLIC_V2,
    Component: PublicTextureDetailsV2,
    key: 'textures/v2/each-model/public/',
    canGuestUserAccess: true,
    canGuestLoginUserAccess: true,
  },
  {
    path: PRODUCT_360_VIEWER_PUBLIC,
    Component: Public360Viewer,
    key: 'product/public/360-viewer/',
    canGuestLoginUserAccess: true,
  },
  {
    path: PRODUCT_360_VIEWER_PUBLIC_V2,
    Component: Public360ViewerV2,
    key: 'product/v2/public/360-viewer/',
    canGuestUserAccess: true,
    canGuestLoginUserAccess: true,
  },
  {
    path: TEXTURE_360_VIEWER_PUBLIC_V2,
    Component: Public360ViewerTextureV2,
    key: 'texture/v2/public/360-viewer/',
    canGuestUserAccess: true,
    canGuestLoginUserAccess: true,
  },
  {
    path: PRODUCT_AI_VIDEO_PUBLIC,
    Component: PublicAIVideo,
    key: 'product/public/ai-video/',
    canGuestLoginUserAccess: true,
  },
  {
    path: PRODUCT_AI_VIDEO_PUBLIC_V2,
    Component: PublicAIVideoV2,
    key: 'v2/product/public/ai-video/',
    canGuestUserAccess: true,
    canGuestLoginUserAccess: true,
  },
  {
    path: PRODUCT_AI_VIDEO_PUBLIC_TEXTURE_V2,
    Component: PublicAIVideoTextureV2,
    key: 'v2/texture/public/ai-video/',
    canGuestUserAccess: true,
    canGuestLoginUserAccess: true,
  },
  {
    path: PRODUCT_VIDEO_PUBLIC,
    Component: PublicVideo,
    key: 'product/public/video/',
    canGuestLoginUserAccess: true,
  },
  {
    path: PRODUCT_VIDEO_PUBLIC_V2,
    Component: PublicVideoV2,
    key: 'v2/product/public/video/',
    canGuestUserAccess: true,
    canGuestLoginUserAccess: true,
  },
  {
    path: TEXTURE_VIDEO_PUBLIC,
    Component: PublicVideoTextureV2,
    key: 'texture/public/video/',
    canGuestUserAccess: true,
    canGuestLoginUserAccess: true,
  },
  {
    path: PRODUCT_PANNING_VIDEO_PUBLIC,
    Component: PublicPanningVideo,
    key: 'product/public/panning-video/',
    canGuestLoginUserAccess: true,
  },
  {
    path: PRODUCT_AR_VIEWER_PUBLIC,
    Component: PublicARViewer,
    key: 'product/public/ar-viewer/',
    canGuestLoginUserAccess: true,
  },
  {
    path: PRODUCT_AR_VIEWER_PUBLIC_V2,
    Component: PublicARViewerV2,
    key: 'v2/product/public/ar-viewer/',
    canGuestUserAccess: true,
    canGuestLoginUserAccess: true,
  },
  {
    path: PRODUCT_3D_VIEWER_PUBLIC_V2,
    Component: PublicProduct3DViewerV2,
    key: 'v2/product/public/3d-viewer/',
    canGuestUserAccess: true,
    canGuestLoginUserAccess: true,
  },
  {
    path: TEXTURE_3D_VIEWER_PUBLIC_V2,
    Component: PublicTexture3DViewerV2,
    key: 'v2/texture/public/3d-viewer/',
    canGuestUserAccess: true,
    canGuestLoginUserAccess: true,
  },
  { path: CONFIGURATOR_VIEWER_ROUTE, Component: ConfiguratorViewer, key: 'configurator-viewer' },
  {
    path: CONFIGURATOR_PUBLIC_VIEWER_ROUTE,
    Component: ConfiguratorPublicViewer,
    key: 'configurator-public-viewer',
    canGuestLoginUserAccess: true,
  },
  {
    path: CONFIGURATOR_PUBLIC_VIEWER_ROUTE_TEXTURE_V2,
    Component: ConfiguratorPublicViewerTextureV2,
    key: 'configurator-public-viewer-texture',
    canGuestUserAccess: true,
    canGuestLoginUserAccess: true,
  },
  {
    path: CONFIGURATOR_PUBLIC_VIEWER_ROUTE_V2,
    Component: ConfiguratorPublicViewerV2,
    key: 'configurator-public-viewer-v2',
    canGuestUserAccess: true,
    canGuestLoginUserAccess: true,
  },
  {
    Component: AdminGuard,
    canGuestUserAccess: true,
    canGuestLoginUserAccess: true,
    routes: [
      {
        Component: PermissionsGuard,
        componentProps: {
          permissions: [PERMISSION.PRODUCT_CONFIGURATORS],
          redirectUrl: '/',
        },
        routes: [
          {
            path: PRODUCT_CONFIGURATOR_ROUTE,
            Component: Configurator,
            key: 'product_configurators/configurator',
            exact: false,
          },
        ],
      },
      // {
      //   Component: PermissionsGuard,
      //   componentProps: {
      //     permissions: [PERMISSION.RETAIL_AUTOMATION],
      //     redirectUrl: '/',
      //   },
      //   routes: [
      //     {
      //       path: RETAIL_AUTOMATION_DETAILS_ROUTE,
      //       Component: RetailAutomation,
      //       key: 'retail_automation',
      //       exact: false,
      //     },
      //   ],
      // },
      // {
      //   path: RETAIL_AUTOMATION_DETAILS_ROUTE_V2,
      //   Component: RetailAutomation,
      //   key: 'retail_automation',
      //   exact: false,
      // },
      {
        Component: PermissionsGuard,
        componentProps: {
          permissions: [PERMISSION.RETAIL_AUTOMATION],
          redirectUrl: '/',
        },
        routes: [
          {
            path: RETAIL_CONFIGURATOR_VIEWER_ROUTE,
            Component: RetailConfiguratorViewer,
            key: 'retail-configurator-viewer',
            exact: false,
          },
          {
            path: RETAIL_AUTOMATION_DETAILS_ROUTE_V2,
            Component: RetailAutomation,
            key: 'retail_automation',
            exact: false,
          },
        ],
      },
      {
        path: ADMIN_MY_PRODUCTS_TEXTURE_EDIT,
        Component: MyProductsTextureEdit,
        key: 'texture_edit',
      },
      {
        path: ADMIN_MY_PRODUCTS_TEXTURE_EDIT_V2,
        Component: MyProductsTextureEdit,
        key: 'texture_edit',
      },
      {
        path: NEW_REQUEST_ROUTE,
        Component: NewRequest,
        key: 'new_request',
        canGuestUserAccess: true,
      },
      {
        path: REQUEST_EDIT_ROUTE,
        Component: NewRequest,
        key: 'request_edit',
        canGuestUserAccess: true,
      },
      {
        path: REQUESTS_ROUTE_PDP_DETAIL,
        Component: RequestsV2Edit,
        key: 'RequestsPDPNew',
        canGuestLoginUserAccess: true,
      },
      { path: ADMIN_MY_PRODUCTS_AIVIDEO_PREVIEW, Component: AIVideoPreview, key: 'product-video' },
      {
        path: ADMIN_MY_PRODUCTS_AIVIDEO_RENDER,
        Component: AIVideoPreview,
        key: 'product-video-render',
      },
      {
        Component: CuratorGuard,
        canGuestLoginUserAccess: true,
        routes: [
          {
            path: ADMIN_CURATOR_ROOM_ID,
            Component: Curator,
            key: 'curator_room',
            canGuestLoginUserAccess: true,
          },
          {
            path: ADMIN_CURATOR_PROJECT_ID,
            Component: Curator,
            key: 'curator_project',
            canGuestLoginUserAccess: true,
          },
        ],
      },
      {
        Component: PermissionsGuard,
        componentProps: {
          permissions: [PERMISSION.UPLOAD_TEXTURE],
          redirectUrl: MY_PRODUCTS_ROUTE,
        },
        routes: [
          {
            path: ADMIN_MY_PRODUCTS_TEXTURE_UPLOAD,
            Component: MyProductsTextureEdit,
            key: 'texture_upload',
          },
          {
            path: ADMIN_MY_PRODUCTS_UPLOADS_MOVE_TO_TEXTURE,
            Component: MyProductsTextureEdit,
            key: 'texture_upload',
          },
        ],
      },
      { path: AI_ROUTE, Component: ImageAI, key: 'image-ai', canGuestUserAccess: true },
      {
        Component: WorkflowBuilderGuard,
        canGuestUserAccess: true,
        routes: [
          {
            path: WORKFLOW_BUILDER_ROUTE,
            Component: WorkflowBuilder,
            key: 'workflow-builder',
          },
        ],
      },
      // {
      //   Component: PermissionsGuard,
      //   componentProps: {
      //     permissions: [PERMISSION.PURCHASE_CREDIT],
      //     redirectUrl: getTransactionsPath(),
      //   },
      //   routes: [{ path: ADD_CREDITS_ROUTE, Component: AddCredits, key: 'add-credits' }],
      // },
    ],
  },
];

export const CURATOR_ROUTES = [ADMIN_CURATOR_ROOM_ID, ADMIN_CURATOR_PROJECT_ID];

export const HIDE_GLOBAL_SEARCH = [
  RETAIL_AUTOMATION_ROUTE_V2,
  RETAIL_CONFIGURATOR_ROUTE,
  REQUESTS_ROUTE_MODELS,
  REQUESTS_ROUTE_PDP,
];

// Trial countdown banner follows the same route gating (hideSearchBar) as the search bar.

export const SHOW_DRILLDOWN_MENU_ROUTES = [
  RENDERS_PREVIEWS_ROUTE,
  RENDERS_PREVIEWS_ROUTE_V2,
  ADMIN_MY_PROPS,
  ADMIN_MY_ROOMS,
  ADMIN_MY_ROOMS_PARAMS,
  MY_PRODUCTS_ROUTE,
  ADMIN_MY_PROJECTS,
  ADMIN_MY_PROJECTS_V2,
  MY_PROJECTS_ROUTE,
  COLLECTIONS_ROUTE,
  TEMPLATES_ROUTE,
  TEMPLATES_ROUTE_V2,
  NEW_PROJECTS_ROUTE,
  ADMIN_MY_PRODUCTS_3DMODAL,
  ADMIN_MY_PRODUCTS_ROOMS,
  ADMIN_MY_PRODUCTS_ROOMS_V2,
  ADMIN_MY_PRODUCTS_TEXTURE,
  ADMIN_MY_PRODUCTS_UPLOADS,
  COLLECTION_ID,
  NEW_DESIGN_ROUTE,
  AUTOMATION_ROUTE,
  AUTOMATION_ROUTE_V2,
  IMAGINE_AI_ROUTE_V2,
  PRODUCT_CONFIGURATORS_ROUTE,
  PRODUCT_CONFIGURATORS_ROUTE_V2,
  PRODUCT_CONFIGURATOR_TEMPLATES_ROUTE,
  ADMIN_MY_PRODUCTS_3DMODAL_V2,
  ADMIN_MY_PRODUCTS_TEXTURE_V2,
  ADMIN_MY_PRODUCTS_UPLOADS_V2,
  PROPS_ROUTE_V2,
  SHARED_PRODUCT_PAGES_ROUTE_V2,
  RETAIL_AUTOMATION_ROUTE,
  PRODUCT_BUILDER_ROUTE,
  ADMIN_MY_PRODUCTS_MATERIAL_V2,
  RETAIL_AUTOMATION_ROUTE_V2
];

export const FULLSCREEN_PRODUCT_VIEWS = [
  // ADMIN_MY_PRODUCTS_3DMODAL_EACH,
  ADMIN_MY_PRODUCTS_TEXTURE_UPLOAD,
];

export const PREVIEW_ROUTES = [
  RENDER_DETAILS,
  PRODUCT_RENDER,
  PROJECT_RENDER,
  TEMPLATES_DETAILS,
  RENDER_DETAILS_V2,
  TEMPLATES_DETAILS_V2,
  // ADMIN_MY_PRODUCTS_3DMODAL_EACH,
  // ADMIN_MY_PRODUCTS_TEXTURE_UPLOAD,
  ADMIN_MY_PRODUCTS_ROOMS_DETAILS,
];

const pathsRequiringLogin = _.chain(_.map(_.concat(NO_LAYOUT_ROUTES, ADMIN_ROUTES), (p) => p.path))
  .concat(SHOW_DRILLDOWN_MENU_ROUTES, PREVIEW_ROUTES)
  .filter((p) => p?.key !== 'forget')
  .value();

export function onAuthPathOnly(currentPathname) {
  return _.includes(pathsRequiringLogin, (rte) => {
    return matchPath({ path: rte }, currentPathname) !== null;
  });
}

export function Routing() {
  const userCtx = useUserContext();
  const { token, isGuestUser, isGuestLogin } = userCtx;
  const location = useLocation();
  const currentPathname = location?.pathname;

  const [isOnTermsAndConditions, setIsOnTermsAndConditions] = useState(true);
  const isTNCModelVisible =
    userCtx.token &&
    userCtx.refreshToken &&
    userCtx.currentUser &&
    (userCtx.latestTNCAccepted === false || userCtx.latestTNCAccepted === 'false');

  const routeMapFunc = (
    {
      exact = true,
      path,
      Component,
      componentProps,
      routes,
      canGuestUserAccess,
      canGuestLoginUserAccess,
    },
    idx
  ) => {
    if (isGuestUser() && !canGuestUserAccess) return null;
    if (isGuestLogin && !canGuestLoginUserAccess) return null;
    return (
      <Route path={path} exact={exact} element={<Component {...componentProps} />} key={idx}>
        {routes?.map(routeMapFunc)}
      </Route>
    );
  };

  const userAccessConfirmed = userCtx.token && userCtx.refreshToken && userCtx.currentUser;

  const displayFullScreen =
    userAccessConfirmed &&
    _.some(FULLSCREEN_PRODUCT_VIEWS, (rte) => {
      return matchPath({ path: rte }, currentPathname) !== null;
    });

  // TODO: refactor routes -> just use AuthGuard instead of this "routing"
  return (
    <>
      {isTNCModelVisible && (
        <TermsAndPrivacyPolicy
          isOnTermsAndConditions={isOnTermsAndConditions}
          setIsOnTermsAndConditions={setIsOnTermsAndConditions}
          isModalVersion={true}
        />
      )}
      <Routes>
        <Route
          element={
            <LayoutWrapper
              sidenav={false}
              token={token}
              refreshToken={userCtx.refreshToken}
              currentUser={userCtx.currentUser}
              fullScreen={displayFullScreen}
            />
          }>
          {ADMIN_NO_SIDEBAR_ROUTES.map(routeMapFunc)}
        </Route>
        <Route
          element={
            <LayoutWrapper
              token={token}
              refreshToken={userCtx.refreshToken}
              currentUser={userCtx.currentUser}
              fullScreen={displayFullScreen}
            />
          }>
          {ADMIN_ROUTES.map(routeMapFunc)}
        </Route>
        {NO_LAYOUT_ROUTES.map(routeMapFunc)}

        <Route path={PRODUCT_WEB_EXPERIENCE_PUBLIC} element={<PublicWebExperience />} />
        <Route path={'not-found'} element={<NotFound />} />
        <Route
          element={
            <LayoutWrapper
              token={userCtx.token}
              refreshToken={userCtx.refreshToken}
              currentUser={userCtx.currentUser}
              fullScreen={displayFullScreen}
            />
          }>
          {LOGIN_ROUTES.map(routeMapFunc)}
        </Route>
        <Route path={AR_VIEWER_ROUTE} element={<ARViewer />} />
        <Route path={PUBLIC_AR_VIEWER_ROUTE} element={<ARViewer />} />
        <Route path={AR_REDIRECT_ROUTE} element={<ArRedirect />} />
        <Route path={PUBLIC_AR_REDIRECT_ROUTE} element={<ArRedirect />} />
        <Route path={ANDROID_CEILING_AR_ROUTE} element={<AndroidCeilingAR />} />
        <Route path={CEILING_CLIP_ROUTE} element={<CeilingClip />} />

        <Route path={'*'} element={<NotFound />} />

        <Route path={PRODUCT_AR_VIEWER_ROUTE} element={<PublicProductARViewer />} />
        <Route path={PRODUCT_AR_VIEWER_ROUTE_PUBLIC} element={<PublicProductARViewer />} />
        <Route path={PRIVATE_AR_ROUTE} element={<PrivateARViewer />} />
        <Route path={SING_UP_GUEST_USER} element={<GuestSignUp />} />
        <Route path={SIGN_UP_GUEST_USER_WITH_TOKEN} element={<GuestRegisterWithToken />} />
        <Route path={LOGIN_HOME} element={<SignIn />} />
      </Routes>
    </>
  );
}
